// useUserLocation - Hook for getting and tracking user's GPS location with reverse geocoding

import { useState, useEffect, useCallback } from 'react';
import type { Coordinates } from '../utils/distance';
import { googleMapsService, type GeocodedLocation } from '../services/googleMapsService';

export type LocationStatus = 'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable' | 'error';

interface UseUserLocationResult {
  location: Coordinates | null;
  status: LocationStatus;
  error: string | null;
  requestLocation: () => void;
  isLoading: boolean;
  // Reverse geocoded address
  address: GeocodedLocation | null;
  addressLoading: boolean;
  addressError: string | null;
  // Get display name for current location
  displayName: string;
}

// Munich city center as default fallback
const MUNICH_CENTER: Coordinates = {
  latitude: 48.1351,
  longitude: 11.5820,
};

export function useUserLocation(): UseUserLocationResult {
  const [location, setLocation] = useState<Coordinates | null>(null);
  const [status, setStatus] = useState<LocationStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Reverse geocoding state
  const [address, setAddress] = useState<GeocodedLocation | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);

  // Perform reverse geocoding when location changes
  const reverseGeocode = useCallback(async (coords: Coordinates) => {
    setAddressLoading(true);
    setAddressError(null);

    try {
      const result = await googleMapsService.reverseGeocode(coords.latitude, coords.longitude);
      if (result) {
        setAddress(result);
      } else {
        // Fallback address
        setAddress({
          latitude: coords.latitude,
          longitude: coords.longitude,
          formattedAddress: 'Aktueller Standort',
        });
      }
    } catch (err) {
      console.error('Reverse geocoding error:', err);
      setAddressError('Adresse konnte nicht ermittelt werden');
      // Set fallback
      setAddress({
        latitude: coords.latitude,
        longitude: coords.longitude,
        formattedAddress: 'Aktueller Standort',
      });
    } finally {
      setAddressLoading(false);
    }
  }, []);

  const requestLocation = useCallback(() => {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setStatus('unavailable');
      setError('Geolocation wird von deinem Browser nicht unterstuetzt');
      return;
    }

    setStatus('requesting');
    setError(null);
    setAddressError(null);

    navigator.geolocation.getCurrentPosition(
      // Success callback
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setLocation(coords);
        setStatus('granted');
        setError(null);

        // Trigger reverse geocoding
        reverseGeocode(coords);
      },
      // Error callback
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setStatus('denied');
            setError('Standortzugriff wurde abgelehnt');
            break;
          case err.POSITION_UNAVAILABLE:
            setStatus('unavailable');
            setError('Standort konnte nicht ermittelt werden');
            break;
          case err.TIMEOUT:
            setStatus('error');
            setError('Zeitueberschreitung bei Standortabfrage');
            break;
          default:
            setStatus('error');
            setError('Unbekannter Fehler bei Standortabfrage');
        }
      },
      // Options
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  }, [reverseGeocode]);

  // Try to get cached permission status on mount
  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted') {
          // Already have permission, get location
          requestLocation();
        } else if (result.state === 'denied') {
          setStatus('denied');
        }
        // For 'prompt', wait for user action
      }).catch(() => {
        // Permissions API not fully supported, that's ok
      });
    }
  }, [requestLocation]);

  // Compute display name
  const displayName = address?.district || address?.city || address?.formattedAddress || 'Aktueller Standort';

  return {
    location,
    status,
    error,
    requestLocation,
    isLoading: status === 'requesting',
    address,
    addressLoading,
    addressError,
    displayName,
  };
}

export { MUNICH_CENTER };
export type { GeocodedLocation };
export default useUserLocation;
